/**
 * Shared utilities for building GitHub issue bodies from governance data.
 * Used by both findings and suggestions pages.
 */

// ── Types ────────────────────────────────────────────────────────

interface FindingForIssue {
  ruleId: string;
  severity: string;
  filePath?: string;
  line?: number;
  rawValue?: string;
  suggestedToken?: string;
  suggestion?: string;
  message: string;
  category?: string;
}

// ── Findings issue body ──────────────────────────────────────────

export function buildFindingsIssueTitle(findings: FindingForIssue[]): string {
  const fileFindings = findings.filter((f) => f.filePath);
  const tokenFindings = findings.filter((f) => !f.filePath);
  const count = findings.length;

  if (tokenFindings.length === count) {
    return `Resolve ${count} duplicate token${count !== 1 ? 's' : ''}`;
  }

  const files = new Set(fileFindings.map((f) => f.filePath!.split('/').pop()));
  if (files.size === 1) {
    return `Fix ${count} token violation${count !== 1 ? 's' : ''} in ${[...files][0]}`;
  }
  return `Fix ${count} token violation${count !== 1 ? 's' : ''} across ${files.size} files`;
}

export function buildFindingsIssueBody(findings: FindingForIssue[]): string {
  const fileFindings = findings.filter((f) => f.filePath);
  const tokenFindings = findings.filter((f) => !f.filePath);

  const uniqueFiles = [
    ...new Set(fileFindings.map((f) => f.filePath)),
  ] as string[];

  const severityCounts: Record<string, number> = {};
  for (const f of findings) {
    severityCounts[f.severity] = (severityCounts[f.severity] ?? 0) + 1;
  }
  const severitySummary = ['error', 'warning', 'info']
    .filter((s) => severityCounts[s])
    .map((s) => `${severityCounts[s]} ${s}`)
    .join(', ');

  const sections: string[] = ['## Design Token Violations', ''];

  // Summary line
  const parts: string[] = [
    `**${findings.length}** finding${findings.length !== 1 ? 's' : ''} (${severitySummary})`,
  ];
  if (uniqueFiles.length > 0) {
    parts.push(
      `across **${uniqueFiles.length}** file${uniqueFiles.length !== 1 ? 's' : ''}`,
    );
  }
  sections.push(parts.join(' ') + '.', '');

  // ── Token-level findings (duplicates, no file context) ──
  if (tokenFindings.length > 0) {
    sections.push('### Duplicate Tokens', '');
    sections.push(
      'These tokens resolve to the same value and should be consolidated:',
    );
    sections.push('');
    for (const f of tokenFindings) {
      sections.push(`- ${f.message}`);
      if (f.suggestion) {
        sections.push(`  - *${f.suggestion}*`);
      }
    }
    sections.push('');
  }

  // ── File-level findings (hardcoded values with location) ──
  if (fileFindings.length > 0) {
    const replacements = fileFindings.filter(
      (f) => f.rawValue && f.suggestedToken,
    );

    // Replacements table
    if (replacements.length > 0) {
      sections.push('### Replacements', '');
      sections.push('| File | Line | Current Value | Replace With |');
      sections.push('|------|------|--------------|-------------|');
      for (const f of replacements) {
        const file = `\`${f.filePath!.split('/').pop()}\``;
        const line = f.line != null ? String(f.line) : '—';
        sections.push(
          `| ${file} | ${line} | \`${f.rawValue}\` | \`var(${f.suggestedToken})\` |`,
        );
      }
      sections.push('');
    }

    // Findings grouped by file
    const byFile = new Map<string, FindingForIssue[]>();
    for (const f of fileFindings) {
      const key = f.filePath!;
      const arr = byFile.get(key);
      if (arr) arr.push(f);
      else byFile.set(key, [f]);
    }

    sections.push('### Affected Files', '');
    for (const [filePath, items] of byFile) {
      sections.push(`**\`${filePath}\`**`);
      for (const f of items) {
        const loc = f.line != null ? `Line ${f.line}` : 'Line ?';
        const fix =
          f.rawValue && f.suggestedToken
            ? ` — replace \`${f.rawValue}\` with \`var(${f.suggestedToken})\``
            : f.suggestion
              ? ` — ${f.suggestion}`
              : '';
        sections.push(`- ${loc}: ${f.message}${fix}`);
      }
      sections.push('');
    }
  }

  // Agent prompt
  sections.push('### Agent Prompt', '');
  sections.push(
    'Use the following structured instructions to fix these violations:',
  );
  sections.push('');
  sections.push('```');
  sections.push(buildAgentPrompt(findings));
  sections.push('```');
  sections.push('');
  sections.push(
    '_Generated by [Fragments Cloud](https://usefragments.com) — AI-powered design system governance._',
  );

  return sections.join('\n');
}

// ── Agent prompt (also used standalone for copy-to-clipboard) ────

export function buildAgentPrompt(findings: FindingForIssue[]): string {
  const fileFindings = findings.filter((f) => f.filePath);
  const tokenFindings = findings.filter((f) => !f.filePath);

  const lines: string[] = [];

  // Token-level findings
  if (tokenFindings.length > 0) {
    lines.push(
      'Consolidate the following duplicate design tokens. These tokens resolve to the same value — keep one canonical token and update all references to use it.',
    );
    lines.push('');
    for (const f of tokenFindings) {
      lines.push(`- ${f.message}`);
      if (f.suggestion) {
        lines.push(`  Action: ${f.suggestion}`);
      }
    }
    lines.push('');
  }

  // File-level findings
  if (fileFindings.length > 0) {
    lines.push(
      'Fix the following design token violations. Replace hardcoded values with design tokens from the design system.',
    );
    lines.push('');

    const byFile = new Map<string, FindingForIssue[]>();
    for (const f of fileFindings) {
      const key = f.filePath!;
      const arr = byFile.get(key);
      if (arr) arr.push(f);
      else byFile.set(key, [f]);
    }

    for (const [filePath, items] of byFile) {
      lines.push(`File: ${filePath}`);
      for (const f of items) {
        if (f.rawValue && f.suggestedToken) {
          const loc = f.line != null ? ` (line ${f.line})` : '';
          lines.push(
            `  - Replace \`${f.rawValue}\` with \`var(${f.suggestedToken})\`${loc}`,
          );
        } else if (f.suggestion) {
          const loc = f.line != null ? ` (line ${f.line})` : '';
          lines.push(`  - ${f.suggestion}${loc}`);
        } else {
          const loc = f.line != null ? ` (line ${f.line})` : '';
          lines.push(`  - ${f.message}${loc}`);
        }
      }
      lines.push('');
    }
  }

  return lines.join('\n').trimEnd();
}
